import React, { useState, useEffect } from "react";
import { Appointment, Company } from "../../../types";
import { formatDateForDisplay, formatTime } from "../../../utils/dateUtils";
import { cleanPhoneNumber, validatePhoneNumber } from "../../../utils/phoneUtils";
import { publicApi } from "../../../services";
import { Input, ConfirmationModal } from "../../shared";
import { CarBrandLogo } from "../../shared/CarBrandLogo";
import { AppHeader } from "../AppHeader";
import { useTheme } from "../../../contexts/ThemeContext";
import { RescheduleAppointmentModal } from "../../admin/AdminAppointments/RescheduleAppointmentModal";

interface AppointmentsListProps {
  phoneNumber?: string;
  onBackToBooking: () => void;
}

export const AppointmentsList: React.FC<AppointmentsListProps> = ({
  phoneNumber: initialPhoneNumber,
  onBackToBooking,
}) => {
  const { company: themeCompany } = useTheme();
  const [company, setCompany] = useState<Company | null>(themeCompany || null);
  
  // Initialize phone number from prop, localStorage, or empty string
  const [phoneNumber, setPhoneNumber] = useState(() => {
    return (
      initialPhoneNumber ||
      localStorage.getItem("bookingPhoneNumber") ||
      ""
    );
  });
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"past" | "future">("future");
  
  // Modal states
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [selectedAppointmentForReschedule, setSelectedAppointmentForReschedule] = useState<Appointment | null>(null);
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false);
  const [selectedAppointmentForCancel, setSelectedAppointmentForCancel] = useState<Appointment | null>(null);

  // Fetch company data
  useEffect(() => {
    const fetchCompany = async () => {
      if (themeCompany) {
        setCompany(themeCompany);
        return;
      }
      try {
        const response = await publicApi.getCompany();
        if (response.success && response.data) {
          setCompany(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch company:", error);
      }
    };

    fetchCompany();
  }, [themeCompany]);

  // Save phone number to localStorage when it changes
  useEffect(() => {
    if (phoneNumber.trim()) {
      localStorage.setItem("bookingPhoneNumber", phoneNumber);
    }
  }, [phoneNumber]);

  // Auto-fetch appointments on mount if phoneNumber exists and is valid
  useEffect(() => {
    const autoFetchAppointments = async () => {
      if (!phoneNumber.trim()) {
        return;
      }

      const phoneValidation = validatePhoneNumber(phoneNumber);
      if (phoneValidation.isValid) {
        await loadAppointments();
      }
    };

    autoFetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const loadAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      const cleanPhone = cleanPhoneNumber(phoneNumber);
      if (!cleanPhone) {
        setLoading(false);
        return;
      }
      const data = await publicApi.getAppointmentsByPhoneNumber(cleanPhone);
      if (data.success && data.data) {
        setAppointments(data.data.filter((appointment: Appointment) => appointment.status !== "cancelled") || []);
      } else {
        setError(data.error || "Falha ao carregar agendamentos");
      }
    } catch (err) {
      setError("Falha ao carregar agendamentos");
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhoneNumber(value);
    if (phoneError) {
      setPhoneError(null);
    }
  };

  const handlePhoneSubmit = () => {
    const phoneValidation = validatePhoneNumber(phoneNumber);

    if (!phoneValidation.isValid) {
      setPhoneError(phoneValidation.error || "Telefone inválido");
      return;
    }

    // Save to localStorage
    localStorage.setItem("bookingPhoneNumber", phoneNumber);
    setPhoneError(null);
    // Load appointments
    loadAppointments();
  };

  const today = new Date().toISOString().split("T")[0];

  const filteredAppointments = appointments
    .filter((appointment) => {
      const appointmentDate = appointment.appointmentDate;

      switch (filter) {
        case "past":
          return appointmentDate < today;
        case "future":
          return appointmentDate >= today;
        default:
          return true;
      }
    })
    .sort((a, b) => {
      // For future appointments, sort ascending (oldest first)
      // For past appointments, sort descending (newest first)
      if (filter === "future") {
        return (
          new Date(a.appointmentDate).getTime() -
          new Date(b.appointmentDate).getTime()
        );
      } else {
        return (
          new Date(b.appointmentDate).getTime() -
          new Date(a.appointmentDate).getTime()
        );
      }
    });

  const getStatusBadge = (appointment: Appointment) => {
    const appointmentDate = appointment.appointmentDate;
    const isPast = appointmentDate < today;

    if (isPast) {
      return (
        <span
          className="inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-semibold 
                       bg-gray-100 text-gray-800 border border-gray-200"
        >
          <span className="mr-1 text-xs sm:text-sm">✅</span>
          Realizado
        </span>
      );
    } else {
      return (
        <span
          className="inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-semibold 
                       bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 
                       border border-emerald-200"
        >
          <span className="mr-1 text-xs sm:text-sm">📅</span>
          Agendado
        </span>
      );
    }
  };

  // Reschedule handlers
  const handleRescheduleClick = (appointment: Appointment) => {
    setSelectedAppointmentForReschedule(appointment);
    setRescheduleModalOpen(true);
  };

  const handleRescheduleClose = () => {
    setRescheduleModalOpen(false);
    setSelectedAppointmentForReschedule(null);
  };

  const handleRescheduleSuccess = async () => {
    setRescheduleModalOpen(false);
    setSelectedAppointmentForReschedule(null);
    await loadAppointments();
  };

  // Cancel handlers
  const handleCancelClick = (appointment: Appointment) => {
    setSelectedAppointmentForCancel(appointment);
    setCancelConfirmationOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedAppointmentForCancel) return;

    try {
      const response = await publicApi.cancelAppointment(
        selectedAppointmentForCancel.id
      );

      if (response.success) {
        await loadAppointments();
        setCancelConfirmationOpen(false);
        setSelectedAppointmentForCancel(null);
      } else {
        setError(response.error || "Erro ao cancelar agendamento");
      }
    } catch (err) {
      console.error("Error canceling appointment:", err);
      setError("Erro ao cancelar agendamento");
    }
  };

  const handleCancelModalClose = () => {
    setCancelConfirmationOpen(false);
    setSelectedAppointmentForCancel(null);
  };

  // Render phone input section (reusable)
  const renderPhoneInput = () => (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-medium p-3 sm:p-4 md:p-6 mb-4 sm:mb-6">
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <div className="flex-1">
          <Input
            id="phoneNumber"
            name="phoneNumber"
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handlePhoneSubmit();
              }
            }}
            placeholder="(11) 99999-9999"
            label="Telefone"
            error={phoneError || undefined}
            disabled={loading}
            className="flex-1"
          />
          {!phoneNumber.trim() && !phoneError && (
            <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-500">
              Digite seu telefone para visualizar seus agendamentos
            </p>
          )}
        </div>
        <div className="flex items-end">
          <button
            onClick={handlePhoneSubmit}
            disabled={loading || !phoneNumber.trim()}
            className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-sky-600 to-sky-700 hover:from-sky-700 
                     hover:to-sky-800 text-white font-bold rounded-lg sm:rounded-xl shadow-medium hover:shadow-large 
                     transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 
                     disabled:cursor-not-allowed disabled:transform-none text-sm sm:text-base h-[44px] sm:h-[52px]"
          >
            {loading ? "Carregando..." : "Buscar"}
          </button>
        </div>
      </div>
    </div>
  );

  if (loading && phoneNumber.trim()) {
    return (
      <div>
        <AppHeader company={company} />
        <main>
          <div className="w-full px-3 sm:px-6 py-4 sm:py-8">
            <div className="w-full max-w-7xl mx-auto animate-fade-in">
              {/* Header */}
              <div className="mb-6 sm:mb-8">
                <button
                  onClick={onBackToBooking}
                  className="group flex items-center gap-2 sm:gap-3 text-sky-600 hover:text-sky-700 
                           font-semibold mb-4 sm:mb-6 transition-all duration-300 hover:gap-3 sm:hover:gap-4 text-sm sm:text-base"
                >
                  <span className="text-lg sm:text-xl transform group-hover:-translate-x-1 transition-transform duration-300">
                    ←
                  </span>
                  <span>Voltar ao Agendamento</span>
                </button>

                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                  <span className="text-2xl sm:text-3xl md:text-4xl">📋</span>
                  <span>Meus Agendamentos</span>
                </h2>

                {renderPhoneInput()}
              </div>

              {/* Loading State */}
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-large p-6 sm:p-8 md:p-12 text-center">
                <div
                  className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-r 
                              from-sky-500 to-sky-600 rounded-full mb-4 sm:mb-6 animate-pulse-soft"
                >
                  <span className="text-xl sm:text-2xl text-white">⏳</span>
                </div>
                <p className="text-base sm:text-lg md:text-xl text-gray-600 font-medium">
                  Carregando seus agendamentos...
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error && phoneNumber.trim()) {
    return (
      <div>
        <AppHeader company={company} />
        <main>
          <div className="w-full px-3 sm:px-6 py-4 sm:py-8">
            <div className="w-full max-w-7xl mx-auto animate-fade-in">
              {/* Header */}
              <div className="mb-6 sm:mb-8">
                <button
                  onClick={onBackToBooking}
                  className="group flex items-center gap-2 sm:gap-3 text-sky-600 hover:text-sky-700 
                           font-semibold mb-4 sm:mb-6 transition-all duration-300 hover:gap-3 sm:hover:gap-4 text-sm sm:text-base"
                >
                  <span className="text-lg sm:text-xl transform group-hover:-translate-x-1 transition-transform duration-300">
                    ←
                  </span>
                  <span>Voltar ao Agendamento</span>
                </button>

                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                  <span className="text-2xl sm:text-3xl md:text-4xl">📋</span>
                  <span>Meus Agendamentos</span>
                </h2>

                {renderPhoneInput()}
              </div>

              {/* Error State */}
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-large p-6 sm:p-8 md:p-12 text-center border-l-4 border-red-500">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-red-100 rounded-full mb-4 sm:mb-6">
                  <span className="text-xl sm:text-2xl text-red-600">⚠️</span>
                </div>
                <p className="text-base sm:text-lg md:text-xl text-gray-600 font-medium mb-3 sm:mb-4 px-2">{error}</p>
                <button
                  onClick={handlePhoneSubmit}
                  className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-sky-600 to-sky-700 hover:from-sky-700 
                           hover:to-sky-800 text-white font-bold rounded-lg sm:rounded-xl shadow-medium hover:shadow-large 
                           transition-all duration-300 transform hover:scale-105 active:scale-95 text-sm sm:text-base"
                >
                  Tentar Novamente
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div>
      <AppHeader company={company} />
      <main>
        <div className="w-full px-3 sm:px-6 py-4 sm:py-8">
          <div className="w-full max-w-7xl mx-auto animate-fade-in">
            {/* Header */}
            <div className="mb-6 sm:mb-8">
              <button
                onClick={onBackToBooking}
                className="group flex items-center gap-2 sm:gap-3 text-sky-600 hover:text-sky-700 
                         font-semibold mb-4 sm:mb-6 transition-all duration-300 hover:gap-3 sm:hover:gap-4 text-sm sm:text-base"
              >
                <span className="text-lg sm:text-xl transform group-hover:-translate-x-1 transition-transform duration-300">
                  ←
                </span>
                <span>Voltar ao Agendamento</span>
              </button>

              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <span className="text-2xl sm:text-3xl md:text-4xl">📋</span>
                <span>Meus Agendamentos</span>
              </h2>

              {renderPhoneInput()}
            </div>

            {/* Filter Tabs */}
            <div className="bg-white rounded-xl sm:rounded-2xl p-1.5 sm:p-2 shadow-medium mb-4 sm:mb-8 flex flex-col sm:flex-row gap-2 sm:gap-1">
              <button
                onClick={() => setFilter("future")}
                className={`flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 text-xs sm:text-sm md:text-base ${
                  filter === "future"
                    ? "bg-gradient-to-r from-sky-500 to-sky-600 text-white shadow-medium"
                    : "text-gray-600 hover:text-sky-600 hover:bg-sky-50"
                }`}
              >
                Próximos (
                {appointments.filter((a) => a.appointmentDate >= today).length})
              </button>
              <button
                onClick={() => setFilter("past")}
                className={`flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 text-xs sm:text-sm md:text-base ${
                  filter === "past"
                    ? "bg-gradient-to-r from-gray-500 to-gray-600 text-white shadow-medium"
                    : "text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                Passados (
                {appointments.filter((a) => a.appointmentDate < today).length})
              </button>
            </div>

            {!phoneNumber.trim() ? (
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-large p-6 sm:p-8 md:p-12 text-center">
                <div className="mb-4 sm:mb-6">
                  <span className="text-5xl sm:text-6xl md:text-8xl">📱</span>
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-2 sm:mb-4">
                  Digite seu telefone
                </h3>
                <p className="text-gray-600 text-sm sm:text-base md:text-lg px-2">
                  Para visualizar seus agendamentos, digite seu número de telefone acima.
                </p>
              </div>
            ) : filteredAppointments.length === 0 ? (
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-large p-6 sm:p-8 md:p-12 text-center">
                <div className="mb-4 sm:mb-6">
                  <span className="text-5xl sm:text-6xl md:text-8xl">📅</span>
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-2 sm:mb-4">
                  Nenhum agendamento encontrado
                </h3>
                <p className="text-gray-600 text-sm sm:text-base md:text-lg px-2">
                  {filter === "future"
                    ? "Você não tem agendamentos futuros."
                    : "Você ainda não realizou nenhum agendamento."}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredAppointments.map((appointment) => {
                  const appointmentDate = appointment.appointmentDate;
                  const isPast = appointmentDate < today;
                  
                  return (
                    <div
                      key={appointment.id}
                      className={`rounded-xl sm:rounded-2xl shadow-medium border-2 transition-all duration-300 hover:shadow-large ${
                        isPast
                          ? "border-gray-200 bg-gray-50 opacity-90 hover:bg-gray-100"
                          : "border-sky-100 bg-white hover:bg-sky-50 hover:border-sky-200"
                      }`}
                    >
                      <div className="p-3 sm:p-4 md:p-6">
                        {/* Mobile Layout */}
                        <div className="block sm:hidden space-y-3">
                          <div className="flex items-start">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                                {appointment.service.name}
                              </h3>
                              {appointment.service.description && (
                                <p className="text-xs sm:text-sm text-gray-500 mt-1 line-clamp-2">
                                  {appointment.service.description}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-start">
                            <div className="flex-1 min-w-0">
                                {getStatusBadge(appointment)}
                              </div>
                          </div>

                          {company?.category === "car" && appointment.clientCar && (
                            <div className="flex items-start">
                              <div className="flex-1 min-w-0">
                                  <p className="text-xs sm:text-sm text-gray-600 mt-1 flex items-center gap-1.5">
                                    <CarBrandLogo brand={appointment.clientCar.brand} />
                                    <span className="truncate">{appointment.clientCar.model} - {appointment.clientCar.plate}</span>
                                  </p>
                              </div>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-2 gap-3 sm:gap-4 pt-2 sm:pt-3 border-t border-gray-100">
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5 sm:mb-1">Data</p>
                              <p className="text-xs sm:text-sm font-semibold text-gray-900">
                                📅 {formatDateForDisplay(appointment.appointmentDate)}
                              </p>
                              {appointment.startTime && appointment.endTime && (
                                <p className="text-xs text-gray-600 mt-0.5 sm:mt-1">
                                  ⏰ {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                                </p>
                              )}
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5 sm:mb-1">Preço</p>
                              <p className="text-base sm:text-lg font-bold text-green-600">
                                R$ {parseFloat(appointment.price).toFixed(2)}
                              </p>
                            </div>
                          </div>
                          
                          {/* Action Buttons - Mobile */}
                          {!isPast && (
                            <div className="flex gap-2 pt-2 sm:pt-3 border-t border-gray-100">
                              <button
                                onClick={() => handleRescheduleClick(appointment)}
                                className="flex-1 px-2 sm:px-3 py-2 bg-gradient-to-r from-sky-600 to-sky-700 hover:from-sky-700 hover:to-sky-800 text-white rounded-lg shadow-medium hover:shadow-large transition-all duration-200 flex items-center justify-center gap-1 sm:gap-2"
                              >
                                <span className="text-sm sm:text-base">📅</span>
                                <span className="font-semibold text-xs sm:text-sm whitespace-nowrap">Reagendar</span>
                              </button>
                              <button
                                onClick={() => handleCancelClick(appointment)}
                                className="flex-1 px-2 sm:px-3 py-2 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 hover:bg-gray-50 rounded-lg shadow-medium hover:shadow-large transition-all duration-200 flex items-center justify-center gap-1 sm:gap-2"
                              >
                                <span className="text-sm sm:text-base">🗑️</span>
                                <span className="font-semibold text-xs sm:text-sm whitespace-nowrap">Cancelar</span>
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Desktop Layout */}
                        <div className="hidden sm:grid sm:grid-cols-12 gap-4 items-center">
                          <div className="col-span-12 md:col-span-4 lg:col-span-3">
                            <h3 className="text-base font-bold text-gray-900 mb-1">
                              {appointment.service.name}
                            </h3>
                            {appointment.service.description && (
                              <p className="text-sm text-gray-500 truncate">
                                {appointment.service.description}
                              </p>
                            )}
                            {company?.category === "car" && appointment.clientCar && (
                              <p className="text-sm text-gray-600 mt-1 flex items-center gap-1.5">
                                <CarBrandLogo brand={appointment.clientCar.brand} />
                                <span>{appointment.clientCar.model} - {appointment.clientCar.plate}</span>
                              </p>
                            )}
                          </div>
                          
                          <div className="col-span-12 md:col-span-3 lg:col-span-3">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">📅</span>
                              <p className="text-sm font-semibold text-gray-900">
                                {formatDateForDisplay(appointment.appointmentDate)}
                              </p>
                            </div>
                            {appointment.startTime && appointment.endTime && (
                              <p className="text-sm text-gray-600">
                                ⏰ {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                              </p>
                            )}
                          </div>
                          
                          <div className="col-span-12 md:col-span-2 lg:col-span-2 flex justify-start md:justify-center">
                            {getStatusBadge(appointment)}
                          </div>
                          
                          <div className="col-span-12 md:col-span-3 lg:col-span-4 flex items-center justify-between md:justify-end gap-4">
                            <div className="text-right">
                              <p className="text-xs text-gray-500 mb-1 hidden md:block">Preço</p>
                              <p className="text-lg font-bold text-green-600">
                                💰 R$ {parseFloat(appointment.price).toFixed(2)}
                              </p>
                            </div>
                            
                            {/* Action Buttons - Desktop */}
                            {!isPast && (
                              <div className="flex gap-2 flex-shrink-0">
                                <button
                                  onClick={() => handleRescheduleClick(appointment)}
                                  className="p-2 bg-gradient-to-r from-sky-600 to-sky-700 hover:from-sky-700 hover:to-sky-800 text-white rounded-lg shadow-medium hover:shadow-large transition-all duration-200"
                                  title="Reagendar"
                                >
                                  <span className="text-lg">📅</span>
                                </button>
                                <button
                                  onClick={() => handleCancelClick(appointment)}
                                  className="p-2 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 hover:bg-gray-50 rounded-lg shadow-medium hover:shadow-large transition-all duration-200"
                                  title="Cancelar"
                                >
                                  <span className="text-lg">🗑️</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
      
      {/* Reschedule Appointment Modal */}
      {rescheduleModalOpen && selectedAppointmentForReschedule && (
        <RescheduleAppointmentModal
          isOpen={rescheduleModalOpen}
          onClose={handleRescheduleClose}
          onSuccess={handleRescheduleSuccess}
          disablePast={true} 
          appointment={selectedAppointmentForReschedule}
          company={company}
        />
      )}

      {/* Cancel Confirmation Modal */}
      <ConfirmationModal
        isOpen={cancelConfirmationOpen}
        title="Cancelar Agendamento"
        message={`Tem certeza que deseja cancelar este agendamento? Esta ação não pode ser desfeita.`}
        confirmText="Cancelar Agendamento"
        cancelText="Voltar"
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelModalClose}
        variant="danger"
      />
    </div>
  );
};
